// A campaign being built in Ask Ogma, beside the chat (Craig, 31 Oct 2026: "it should walk me through what is needed,
// or open it in the window so I can walk through it there"). The things a campaign needs, in the order Ogma asks them,
// each ticked when it is there. The person can change any of them here, or press Ask me to have Ogma ask in the chat;
// either way the draft beside the chat is the one Ogma builds on (it goes back with every question). Nothing is saved
// until Review and confirm.
import { useState, type FormEvent } from "react";
import { Check, Pencil } from "lucide-react";
import type { CampaignDraft } from "../../lib/chat";
import { REACH_LABEL } from "../../lib/campaignReach";
import { draftSteps, withStep, type DraftStep } from "../../lib/sideWindow";

function Edit({ step, onSave, onCancel }: { step: DraftStep; onSave: (raw: string) => void; onCancel: () => void }) {
  const [raw, setRaw] = useState(step.raw);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave(raw);
  };
  const field = step.input === "reach" ? (
    <select aria-label={step.label} value={raw} onChange={(e) => setRaw(e.target.value)} autoFocus>
      {(["new", "known", "both"] as const).map((r) => <option key={r} value={r}>{REACH_LABEL[r]}</option>)}
    </select>
  ) : step.input === "long" ? (
    <textarea aria-label={step.label} value={raw} rows={3} onChange={(e) => setRaw(e.target.value)} autoFocus />
  ) : (
    <input aria-label={step.label} type={step.input === "number" ? "number" : "text"} value={raw} onChange={(e) => setRaw(e.target.value)} autoFocus />
  );
  return (
    <form className="checklist-edit" onSubmit={submit}>
      {field}
      <span className="chat-confirm-buttons">
        <button type="submit" className="btn btn-small btn-primary">Save</button>
        <button type="button" className="btn btn-small btn-quiet" onClick={onCancel}>Cancel</button>
      </span>
    </form>
  );
}

export function CampaignChecklist({ draft, onChange, onAsk }: { draft: CampaignDraft; onChange?: (d: CampaignDraft) => void; onAsk?: (text: string) => void }) {
  const [editing, setEditing] = useState<DraftStep["key"] | null>(null);
  const steps = draftSteps(draft);
  const needed = steps.filter((s) => s.needed);
  const done = needed.filter((s) => s.value).length;
  const next = needed.find((s) => !s.value) ?? null;
  return (
    <div className="campaign-checklist">
      <p className="small muted" role="status">
        {next ? `${done} of ${needed.length} done. Next: ${next.label.toLowerCase()}.` : "Everything Ogma needs is here. Check it, then Review and confirm."}
      </p>
      <ol className="checklist-steps">
        {steps.map((s) => (
          <li key={s.key} className={s.value ? "is-done" : s === next ? "is-next" : undefined}>
            <span className="checklist-mark" aria-hidden="true">{s.value ? <Check size={14} /> : null}</span>
            <div className="checklist-body">
              <div className="checklist-label">
                <strong>{s.label}</strong>
                {!s.needed && <span className="muted small"> (optional)</span>}
                {onChange && editing !== s.key && (
                  <button type="button" className="btn-icon" aria-label={`Change ${s.label.toLowerCase()}`} onClick={() => setEditing(s.key)}>
                    <Pencil size={14} aria-hidden="true" focusable="false" />
                  </button>
                )}
              </div>
              {onChange && editing === s.key ? (
                <Edit step={s} onCancel={() => setEditing(null)} onSave={(raw) => {
                  onChange(withStep(draft, s.key, raw));
                  setEditing(null);
                }} />
              ) : s.value ? (
                <span className="small">{s.value}</span>
              ) : (
                <span className="small muted">
                  Still to do.{" "}
                  {onAsk && (
                    <button type="button" className="link-btn small" onClick={() => onAsk(`Help me with ${s.label.toLowerCase()} for this campaign`)}>
                      Ask me
                    </button>
                  )}
                </span>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
